import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Document {
  id: string;
  tenantId: string;
  folderId: string | null;
  clientId: string | null;
  clientName: string | null;
  jobId: string | null;
  jobTitle: string | null;
  fileName: string;
  fileType: string;
  fileSize: number;
  mimeType: string;
  storagePath: string;
  uploadedById: string;
  uploadedByName: string;
  scanStatus: ScanStatus;
  scanResult: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ScanStatus = 'pending' | 'scanning' | 'clean' | 'infected' | 'error';

export interface Folder {
  id: string;
  tenantId: string;
  parentId: string | null;
  name: string;
  path: string;
  clientId: string | null;
  /** Resolved client display name (present on client root folders from the tree endpoint). */
  clientName: string | null;
  jobId: string | null;
  createdAt: string;
  children: Folder[];
}

export interface DocumentRequest {
  id: string;
  tenantId: string;
  clientId: string;
  clientName: string;
  jobId: string | null;
  jobTitle: string | null;
  requestedById: string;
  requestedByName: string;
  requiredDocuments: RequiredDocument[];
  dueDate: string | null;
  notes: string | null;
  status: DocumentRequestStatus;
  createdAt: string;
  updatedAt: string;
}

export type DocumentRequestStatus = 'pending' | 'partially_fulfilled' | 'fulfilled' | 'overdue';

export interface RequiredDocument {
  id: string;
  documentType: string;
  description: string | null;
  isMandatory: boolean;
  isFulfilled: boolean;
  fulfilledDocumentId: string | null;
}

// ─── Request Types ───────────────────────────────────────────────────────────

export interface DocumentsListParams {
  folderId?: string | null;
  clientId?: string;
  jobId?: string;
  includeLinked?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface CreateFolderRequest {
  name: string;
  parentId: string | null;
  clientId?: string;
  jobId?: string;
}

export interface UploadDocumentRequest {
  file: File;
  folderId?: string | null;
  clientId?: string;
  jobId?: string;
}

export interface CreateDocumentRequestPayload {
  clientId: string;
  jobId?: string;
  requiredDocuments: { documentType: string; description?: string; isMandatory?: boolean }[];
  dueDate?: string;
  notes?: string;
}

export interface DocumentRequestsListParams {
  clientId?: string;
  status?: DocumentRequestStatus | '';
  page?: number;
  pageSize?: number;
}

/** Paginated response */
interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ─── Query Keys ──────────────────────────────────────────────────────────────

const documentKeys = {
  all: ['documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  list: (params: DocumentsListParams) => [...documentKeys.lists(), params] as const,
  folders: () => [...documentKeys.all, 'folders'] as const,
  folderTree: (parentId?: string | null) => [...documentKeys.folders(), { parentId }] as const,
  requests: () => [...documentKeys.all, 'requests'] as const,
  requestList: (params: DocumentRequestsListParams) =>
    [...documentKeys.requests(), params] as const,
};

// ─── Hooks ───────────────────────────────────────────────────────────────────

/**
 * Hook to fetch paginated list of documents with filtering.
 * Validates: Requirements 9.16
 */
export function useDocumentsList(params: DocumentsListParams = {}) {
  return useQuery({
    queryKey: documentKeys.list(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.folderId) queryParams.set('folderId', params.folderId);
      if (params.clientId) queryParams.set('clientId', params.clientId);
      if (params.jobId) queryParams.set('jobId', params.jobId);
      if (params.includeLinked) queryParams.set('include_linked', 'true');
      if (params.page) queryParams.set('page', String(params.page));
      if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));
      if (params.search) queryParams.set('search', params.search);
      if (params.sortBy) queryParams.set('sortBy', params.sortBy);
      if (params.sortDirection) queryParams.set('sortDirection', params.sortDirection);

      const response = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination: { page: number; pageSize: number; total: number; totalPages: number };
      }>(`/documents?${queryParams.toString()}`);

      const raw = response.data.data || [];
      const items: Document[] = raw.map((d) => ({
        id: d.id as string,
        tenantId: (d.tenant_id || d.tenantId || '') as string,
        folderId: (d.folder_id || d.folderId || null) as string | null,
        clientId: (d.client_id || d.clientId || null) as string | null,
        clientName: (d.client_name || d.clientName || null) as string | null,
        jobId: (d.job_id || d.jobId || null) as string | null,
        jobTitle: (d.job_title || d.jobTitle || null) as string | null,
        fileName: (d.file_name || d.fileName || '') as string,
        fileType: (d.file_type || d.fileType || '') as string,
        fileSize: Number(d.file_size_bytes || d.fileSize || 0),
        mimeType: (d.file_type || d.mimeType || '') as string,
        storagePath: (d.s3_key || d.storagePath || '') as string,
        uploadedById: (d.uploaded_by_id || d.uploadedById || '') as string,
        uploadedByName: (d.uploaded_by_name || d.uploadedByName || '') as string,
        scanStatus: (d.malware_scan_status || d.scanStatus || 'pending') as ScanStatus,
        scanResult: (d.scan_result || d.scanResult || null) as string | null,
        createdAt: (d.created_at || d.createdAt || '') as string,
        updatedAt: (d.updated_at || d.updatedAt || '') as string,
      }));

      const pagination = response.data.pagination || {
        page: 1,
        pageSize: 25,
        total: items.length,
        totalPages: 1,
      };

      return {
        items,
        total: pagination.total,
        page: pagination.page,
        pageSize: pagination.pageSize,
      };
    },
  });
}

/**
 * Hook to fetch the folder tree structure.
 * Returns nested folders for tree navigation.
 */
export function useFolders(parentId?: string | null) {
  return useQuery({
    queryKey: documentKeys.folderTree(parentId),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (parentId) queryParams.set('parentId', parentId);

      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        `/documents/folders?${queryParams.toString()}`,
      );
      const raw = response.data.data || [];
      return raw.map(mapFolder);
    },
  });
}

function mapFolder(raw: Record<string, unknown>): Folder {
  return {
    id: (raw.id || '') as string,
    tenantId: (raw.tenant_id || raw.tenantId || '') as string,
    parentId: (raw.parent_folder_id || raw.parentId || null) as string | null,
    name: (raw.name || '') as string,
    path: (raw.path || raw.name || '') as string,
    clientId: (raw.client_id || raw.clientId || null) as string | null,
    clientName: (raw.client_name || raw.clientName || null) as string | null,
    jobId: (raw.job_id || raw.jobId || null) as string | null,
    createdAt: (raw.created_at || raw.createdAt || '') as string,
    children: Array.isArray(raw.children)
      ? (raw.children as Record<string, unknown>[]).map(mapFolder)
      : [],
  };
}

/**
 * Hook to fetch the full nested folder tree for the tenant, grouped by client.
 * Each client's root folder is a top-level node (labelled with the client name)
 * with its subfolders nested underneath — used for Documents page navigation.
 */
export function useFolderTree() {
  return useQuery({
    queryKey: [...documentKeys.folders(), 'tree'] as const,
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/documents/folders/tree');
      return (response.data.data || []).map(mapFolder);
    },
  });
}

/**
 * Hook to create a new folder.
 */
export function useCreateFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateFolderRequest) => {
      const response = await apiClient.post<ApiResponse<Folder>>('/documents/folders', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.folders() });
    },
  });
}

/**
 * Hook to upload a document.
 * Uses multipart/form-data for file upload.
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UploadDocumentRequest) => {
      const formData = new FormData();
      formData.append('file', data.file);
      if (data.folderId) formData.append('folderId', data.folderId);
      if (data.clientId) formData.append('clientId', data.clientId);
      if (data.jobId) formData.append('jobId', data.jobId);

      const response = await apiClient.post<ApiResponse<Document>>('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

/**
 * Hook to delete a document.
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentId: string) => {
      await apiClient.delete(`/documents/${documentId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
    },
  });
}

/**
 * Hook to fetch document requests with filtering.
 * Validates: Requirements 9.3
 */
export function useDocumentRequests(params: DocumentRequestsListParams = {}) {
  return useQuery({
    queryKey: documentKeys.requestList(params),
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params.clientId) queryParams.set('clientId', params.clientId);
      if (params.status) queryParams.set('status', params.status);
      if (params.page) queryParams.set('page', String(params.page));
      if (params.pageSize) queryParams.set('pageSize', String(params.pageSize));

      const response = await apiClient.get<ApiResponse<PaginatedResponse<DocumentRequest>>>(
        `/documents/requests?${queryParams.toString()}`,
      );
      return response.data.data;
    },
  });
}

/**
 * Hook to create a document request for a client.
 */
export function useCreateDocumentRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateDocumentRequestPayload) => {
      const response = await apiClient.post<ApiResponse<DocumentRequest>>('/documents/requests', {
        client_id: data.clientId,
        job_id: data.jobId || undefined,
        items: data.requiredDocuments.map((doc) => ({
          document_type: doc.documentType,
          description: doc.description || undefined,
          is_mandatory: doc.isMandatory,
        })),
        due_date: data.dueDate || undefined,
        notes: data.notes || undefined,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.requests() });
    },
  });
}
